import { AgencyId, formatAddress } from "@inrev/common";
import { type CompanyCheckResponse } from "@portal/api/account/admin";
import { useAdminFetchAgencyPreviews } from "@portal/api/agency/admin";
import { FormItemLabel } from "@portal/components/form";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { Modal } from "@portal/components/layout/Modal";
import { Button } from "@portal/components/ui/Button";
import { Card } from "@portal/components/ui/Card";
import { Fragment } from "react";
import { useNavigate } from "react-router-dom";

type AdminDuplicateCompaniesModalProps = {
	companyChecks: CompanyCheckResponse[];
	onClickOutside: () => void;
	isFullSubmission?: boolean;
};

const textStyles = "text-[13px] text-primary text-wrap";

export const AdminDuplicateCompaniesModal = ({
	companyChecks,
	onClickOutside,
	isFullSubmission,
}: AdminDuplicateCompaniesModalProps) => {
	const navigate = useNavigate();
	const { agencyPreviews } = useAdminFetchAgencyPreviews();

	// create a name map so we don't need to search through an array for each company
	const agencyIdNameMap: Record<AgencyId, string> = {};
	if (agencyPreviews) {
		agencyPreviews.forEach((agency) => {
			agencyIdNameMap[agency.id] = agency.displayName;
		});
	}

	return (
		<Modal onClickOutside={onClickOutside}>
			<Card className="px-8 py-6">
				<div className="pb-3 font-[550]">Possible duplicate companies found</div>
				<div className="h-[50vh] min-w-[600px] block overflow-y-auto">
					<div className="h-full w-full flex flex-col space-y-4">
						{companyChecks.length > 0 &&
							companyChecks.map((check) => {
								const card = (
									<div
										className="shrink-0 flex flex-col gap-4 border border-bc-default hover:border-bc-stronger cursor-pointer rounded-md px-4 py-3"
										onClick={
											isFullSubmission
												? () => navigate(`/accounts/${check.suretyAccountId}`)
												: undefined
										}
									>
										{check.agencyId && agencyIdNameMap[check.agencyId] && (
											<div className="flex flex-col items-start">
												<FormItemLabel>Agency Name:</FormItemLabel>
												<div className={textStyles}>{agencyIdNameMap[check.agencyId]}</div>
											</div>
										)}
										<div className="grid grid-cols-3 gap-2 w-full">
											<div className="flex flex-col items-start">
												<FormItemLabel>Company Name:</FormItemLabel>
												<div className={textStyles}>{check.companyName}</div>
											</div>
											<div className="flex flex-col items-start">
												<FormItemLabel>FEIN:</FormItemLabel>
												<div className={textStyles}>{check.fein}</div>
											</div>
											<div className="flex flex-col items-start">
												<FormItemLabel>Account Name:</FormItemLabel>
												<div className={textStyles}>{check.suretyAccountName}</div>
											</div>
										</div>
										<div className="flex flex-col items-start">
											<FormItemLabel>Company Address:</FormItemLabel>
											<div className={textStyles}>{formatAddress(check.companyAddress)}</div>
										</div>
									</div>
								);

								if (isFullSubmission)
									return <Fragment key={check.companyContactId}>{card}</Fragment>;

								return (
									<ConfirmationModalClickWrapper
										message="Are you sure you want to leave? All changes will be lost."
										confirmButtonText="Leave Page"
										cancelButtonText="Stay on This Page"
										confirmationButtonColor="danger"
										shouldConfirm={true}
										onConfirm={() => {
											navigate(`/accounts/${check.suretyAccountId}`);
										}}
										key={check.companyContactId}
									>
										{card}
									</ConfirmationModalClickWrapper>
								);
							})}
					</div>
				</div>
				<Button onClick={onClickOutside} className="mt-6 w-full" color="primary" filled>
					This is a new account
				</Button>
			</Card>
		</Modal>
	);
};
